import * as React from 'react';
import { Globe, Lock, Users, ShieldOff, Image } from 'lucide-react';
import { EventProfileRow } from 'cb-model';
import { EventFlowOptions, VisibilityLimitOptions } from 'cb-model/SharedOptions';
import styles from './EventProfileCard.module.css';

export interface EventProfileCardProps {
  profile: EventProfileRow;
  selected?: boolean;
  onClick?: () => void;
}

const VISIBILITY_ICONS: Record<string, React.ReactNode> = {
  public: <Globe size={13} />,
  private: <Lock size={13} />,
  'private-group': <Users size={13} />,
  closed: <ShieldOff size={13} />,
};

const VISIBILITY_COLOUR: Record<string, string> = {
  public: '#0d6efd',
  private: '#6c757d',
  'private-group': '#6c757d',
  closed: '#dc3545',
};

const FLOW_BADGES: { flowOptions: string[]; label: string }[] = [
  { flowOptions: [EventFlowOptions[1].id, EventFlowOptions[3].id], label: 'Record Attendance' },
  { flowOptions: [EventFlowOptions[2].id, EventFlowOptions[3].id], label: 'Capture Feedback' },
  { flowOptions: [EventFlowOptions[2].id, EventFlowOptions[3].id], label: 'Provide Certificate' },
];

const getVisibilityLabel = (visibilityLimit: string): string => {
  const option = VisibilityLimitOptions.find((o) => o.id === visibilityLimit);
  if (!option) return visibilityLimit;
  // Return short label (before the dash)
  return option.label.split(' - ')[0];
};

export const EventProfileCard = (props: EventProfileCardProps): React.JSX.Element => {
  const { profile, selected, onClick } = props;

  const visibilityIcon = VISIBILITY_ICONS[profile.visibility_limit] ?? <Lock size={13} />;
  const visibilityLabel = getVisibilityLabel(profile.visibility_limit);

  return (
    <div
      className={`${styles.card} ${selected ? styles.cardSelected : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      aria-pressed={selected}
    >
      {profile.image ? (
        <img src={profile.image} alt={profile.title} className={styles.image} />
      ) : (
        <div className={styles.imagePlaceholder}>
          <Image size={28} aria-hidden="true" />
        </div>
      )}

      <div className={styles.title} title={profile.title}>
        {profile.title}
      </div>

      <div className={styles.visibilityRow} style={{ color: VISIBILITY_COLOUR[profile.visibility_limit] }}>
        {visibilityIcon}
        <span>{visibilityLabel}</span>
      </div>

      <div className={styles.badges}>
        {FLOW_BADGES.map((b) => (
          <span
            key={b.label}
            className={b.flowOptions.includes(profile.flow_option) ? styles.badge : styles.badgeInactive}
          >
            {b.label}
          </span>
        ))}
      </div>
    </div>
  );
};
